import React, { useState } from 'react';
import { Headphones, Volume2, VolumeX } from 'lucide-react';
import {
    angleIndexOf,
    angleLabel,
    angleOffset,
    audioAngleIndexOf,
    clipAngles,
    isVideoAssetType,
} from '../state/timelineModel';

/**
 * The angle bar, under the timeline.
 *
 * It used to live in the inspector, at the top of the screen beside the
 * picture. Cutting between cameras is a timeline gesture -- split here, pick a
 * camera for this piece, split again -- so every cut meant travelling to the
 * far corner of the window and back. It sits under the tracks now, next to the
 * thing it acts on.
 *
 * It follows the playhead as well as the selection, so scrubbing to a segment
 * is enough to be able to recut it; that is how a multicam editor behaves, and
 * it saves selecting a clip you are already parked on.
 */
function AnglesPanel({
    clip, assets, assetsByFileName, editable,
    onSetAngle, onAddAngle, onUpdateMulticam, onAlignAngles,
}) {
    const [aligning, setAligning] = useState(false);
    const [alignReport, setAlignReport] = useState(null);

    /* Nothing to show with no clip under the playhead, and a still has no take
       to be another view of. Placed after the hooks, which have to run on every
       render whatever is selected. */
    const asset = assetsByFileName.get(String(clip?.asset_filename || ''));
    if (!clip || !isVideoAssetType(asset?.FileType)) return null;

    const angles = clipAngles(clip);
    const liveIndex = angleIndexOf(clip);
    const soundIndex = audioAngleIndexOf(clip);

    const used = new Set(angles.map(angle => angle.asset_filename));
    if (angles.length === 0) used.add(String(clip.asset_filename || ''));
    const addable = assets.filter(asset => (
        isVideoAssetType(asset.FileType) && !used.has(String(asset.FileName))
    ));

    const nameOf = (angle, index) => {
        const asset = assetsByFileName.get(String(angle.asset_filename || ''));
        return asset?.FileAlias || angle.asset_filename || angleLabel(angle, index);
    };

    const setOffset = (index, seconds) => {
        onUpdateMulticam(clip.id, {
            angles: angles.map((angle, i) => (i === index ? { ...angle, offset: seconds } : angle)),
        });
    };

    /**
     * Fill the offsets in from the sound itself.
     *
     * Only the angles it could actually line up are written. One it could not
     * keeps whatever offset it had and is named, because the failure a measured
     * offset must never produce is a confident wrong number: nobody can spot a
     * 200ms error by looking at the timeline, and it would only surface as a
     * cut that feels vaguely off.
     */
    const runAlignment = async () => {
        setAligning(true);
        setAlignReport(null);
        try {
            const measured = await onAlignAngles(
                angles.map(angle => angle.asset_filename),
                angles[0]?.asset_filename,
            );
            const byFileName = new Map(
                (Array.isArray(measured) ? measured : []).map(entry => [String(entry.FileName), entry])
            );

            const unsure = [];
            const drifting = [];
            const next = angles.map((angle, index) => {
                const found = byFileName.get(String(angle.asset_filename));
                if (!found || !found.Trustworthy) {
                    unsure.push(nameOf(angle, index));
                    return angle;
                }
                if (Math.abs(Number(found.DriftSeconds) || 0) > 0.1) {
                    drifting.push(`${nameOf(angle, index)} (${Number(found.DriftSeconds).toFixed(2)}s)`);
                }
                return { ...angle, offset: Number(found.OffsetSeconds) || 0 };
            });

            onUpdateMulticam(clip.id, { angles: next });
            setAlignReport({
                tone: unsure.length > 0 ? 'warn' : 'ok',
                text: [
                    unsure.length > 0
                        ? `Could not line up ${unsure.join(', ')} — set ${unsure.length > 1 ? 'those' : 'that one'} by hand.`
                        : 'Lined up from the sound.',
                    drifting.length > 0
                        ? `These drift by the end of the take: ${drifting.join(', ')}. Long takes may need a second cut point.`
                        : '',
                ].filter(Boolean).join(' '),
            });
        } catch (error) {
            setAlignReport({ tone: 'warn', text: error?.message || 'Could not line these cameras up.' });
        } finally {
            setAligning(false);
        }
    };

    return (
        <div className="nle-angles nle-angles-bar">
            <span className="nle-angles-label">
                Angles
                <em>{asset?.FileAlias || clip.asset_filename}</em>
                {angles.length > 1 && <em>{`${liveIndex + 1} of ${angles.length}`}</em>}
            </span>

            {angles.map((angle, index) => (
                <div key={`${angle.asset_filename}-${index}`} className={`nle-angle-row ${index === liveIndex ? 'live' : ''}`}>
                    <button
                        type="button"
                        className="nle-angle-pick"
                        disabled={!editable}
                        aria-pressed={index === liveIndex}
                        onClick={() => onSetAngle(clip.id, index)}
                        title={index === liveIndex ? 'Showing this camera' : 'Cut to this camera'}
                    >
                        <span className="nle-angle-number">{index + 1}</span>
                        <span className="nle-angle-name">{nameOf(angle, index)}</span>
                    </button>

                    <label className="nle-angle-offset" title="How far this camera started after the first one">
                        <input
                            type="number"
                            step="0.01"
                            value={angleOffset(angle)}
                            disabled={!editable || index === 0}
                            onChange={(e) => setOffset(index, Number(e.target.value))}
                        />
                        <span>s</span>
                    </label>

                    <button
                        type="button"
                        className={`nle-angle-sound ${index === soundIndex ? 'on' : ''}`}
                        disabled={!editable}
                        aria-pressed={index === soundIndex}
                        onClick={() => onUpdateMulticam(clip.id, { audioAngleIndex: index })}
                        title={index === soundIndex
                            ? 'The sound comes from this camera'
                            : 'Take the sound from this camera instead'}
                    >
                        {index === soundIndex ? <Volume2 size={14} aria-hidden="true" /> : <VolumeX size={14} aria-hidden="true" />}
                    </button>
                </div>
            ))}

            {editable && angles.length > 1 && onAlignAngles && (
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={aligning}
                    onClick={runAlignment}
                    title="Work out the offsets from what the cameras heard"
                >
                    {aligning ? 'Listening…' : <><Headphones size={14} aria-hidden="true" /> Line up by sound</>}
                </button>
            )}

            {alignReport && (
                <p className={`nle-align-report ${alignReport.tone}`}>{alignReport.text}</p>
            )}

            {editable && addable.length > 0 && (
                <label className="nle-field">
                    <span className="nle-field-label">
                        {angles.length === 0 ? 'Add another camera of this take' : 'Add another camera'}
                    </span>
                    <select
                        value=""
                        onChange={(e) => {
                            if (!e.target.value) return;
                            onAddAngle(clip.id, { asset_filename: e.target.value, offset: 0 });
                        }}
                    >
                        <option value="">Choose…</option>
                        {addable.map(asset => (
                            <option key={asset.FileName} value={asset.FileName}>
                                {asset.FileAlias || asset.FileName}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {angles.length > 1 && (
                <p className="nle-inspector-hint">
                    Press S to cut, then pick the camera for each piece. The offset is
                    how long after camera 1 this one started rolling — the sound comes
                    from the camera marked with the speaker throughout, whichever is on screen.
                </p>
            )}
        </div>
    );
}

export default AnglesPanel;
